import { useEffect } from 'react';

/**
 * A defensive hook to prevent runtime errors from misconfigured Google Tag Manager tags.
 * Specifically, it handles cases where a tag attempts to access `window.DIL` (from Adobe Audience Manager)
 * when the library is not loaded.
 * This prevent errors for local development and environments where the library is not used.
 */
export const useGTMErrorHandler = () => {
	useEffect(() => {
		// This code runs once on the client-side when the app mounts.
		stubDIL();
		stubUtag();
		stubSe();
		stubScarabQueue();
		stubDataLayer();
		stubGaq();
	}, []); // Empty dependency array ensures this runs only once.
	const stubDIL = () => {
		if (typeof window !== 'undefined' && !(window as any).DIL) {
			console.warn(
				'GTM Safety Stub: Creating a dummy `DIL` object on the window to prevent errors from Adobe Audience Manager tags.'
			);
			(window as any).DIL = createRecursiveProxy();
		}
	};

	const stubUtag = () => {
		if (typeof window !== 'undefined' && !(window as any).utag) {
			console.warn(
				'GTM Safety Stub: Creating a dummy `utag` object on the window to prevent errors from Tealium tags.'
			);
			(window as any).utag = createRecursiveProxy();
		}
	};

	const stubSe = () => {
		if (typeof window !== 'undefined' && !(window as any).se) {
			console.warn(
				'GTM Safety Stub: Creating a dummy `se` object on the window to prevent errors from Adobe Analytics tags.'
			);
			(window as any).se = createRecursiveProxy();
		}
	};

	const stubScarabQueue = () => {
		if (typeof window !== 'undefined' && !(window as any).ScarabQueue) {
			console.warn(
				'GTM Safety Stub: Creating a dummy `ScarabQueue` array on the window to prevent errors from Emarsys tags.'
			);
			(window as any).ScarabQueue = [];
		}
	};

	const stubDataLayer = () => {
		if (typeof window !== 'undefined' && !(window as any).dataLayer) {
			console.warn(
				'GTM Safety Stub: Creating a dummy `dataLayer` array on the window to prevent GTM errors.'
			);
			(window as any).dataLayer = [];
		}
	};

	const stubGaq = () => {
		if (typeof window !== 'undefined' && !(window as any)._gaq) {
			console.warn(
				'GTM Safety Stub: Creating a dummy `_gaq` array on the window to prevent legacy GA errors.'
			);
			(window as any)._gaq = [];
		}
	};
};

/**
 * Creates a recursive proxy that can handle any level of nested property access and method calls.
 * @param name The name of the global object being stubbed (for logging purposes).
 * @returns A new Proxy object.
 */
const createRecursiveProxy = () => {
	const dummy = () => undefined;
	const handler: ProxyHandler<typeof dummy> = {
		get: () => new Proxy(dummy, handler),
		apply: () => new Proxy(dummy, handler),
		construct: () => new Proxy(dummy, handler),
	};

	return new Proxy(dummy, handler);
};
